import { Injectable } from '@angular/core';
import {
  AccountingCode,
  AutoResolveChange,
  AutoResolveChangeType,
  ExplodedOrderLine,
  OrderEntry,
  OrderEntryComparativePrice,
  OrderEntryType,
  ResolveOption,
  ResolveOptionActionType,
  Unit,
} from '../../../../core/model';
import { Occ } from '../../../../core/occ/occ-models';
import { Converter, ConverterService } from '../../../../core/util';
import { convertOrderLineMessages } from '../../../checkout/base/utils/utils';
import {
  CUTTING_NORMALIZER,
  ORDER_CONDITION_NORMALIZER,
  ORDER_ENTRY_NORMALIZER,
  PRE_PRINTED_LABEL_NORMALIZER,
  REEL_CUTTING_NORMALIZER,
} from './converters';

@Injectable({ providedIn: 'root' })
export class OrderEntryNormalizer implements Converter<Occ.OrderEntry, OrderEntry> {
  constructor(private converter: ConverterService) {}

  convert(source: Occ.OrderEntry, target?: OrderEntry): OrderEntry {
    if (source) {
      target = this.convertOrderEntry(source);
    }

    return target;
  }

  private convertOrderEntry(source: Occ.OrderEntry): OrderEntry {
    if (source) {
      const {
        pyOrderLineParameters,
        autoResolveChange,
        resolveOptions,
        conditions,
        article,
        product,
        quantity,
        unitsData,
        messages,
        type,
        ...target
      } = source;
      return {
        ...target,
        ...this.convertPyOrderLineParameters(pyOrderLineParameters),
        type: type as OrderEntryType,
        articleRef: article?.code || product?.code,
        quantity: this.convertQuantity(source),
        unit: this.convertUnitsData(source),
        autoResolveChange: this.convertAutoResolveChange(autoResolveChange),
        resolveOptions: this.convertResolveOptions(resolveOptions),
        conditions: this.converter.convertMany(conditions, ORDER_CONDITION_NORMALIZER),
        messages: convertOrderLineMessages(messages),
        discount: this.convertDiscount(pyOrderLineParameters, source),
        accountingCodes: article?.accountingCodes as AccountingCode[],
        explodedOrderLines: this.convertExplodedOrderLines(source),
        showCustomerSelectedUnit: source?.unitsData?.showCustomerSelectedUnit,
      };
    } else {
      return undefined;
    }
  }

  private convertExplodedOrderLines(source: Occ.OrderEntry): ExplodedOrderLine[] {
    return source.pyOrderLineParameters?.explodedOrderLines?.map((explodedOrderLine) => {
      const nameMarkingTextLines = source.configurationInfos?.find(
        (configurationInfo) =>
          configurationInfo.qualifier === 'name-marking' &&
          configurationInfo.extendedIdentifier === explodedOrderLine.itemPosition
      );
      return {
        ...explodedOrderLine,
        nameMarkingTextLines: nameMarkingTextLines?.configurationTextValues
          ? nameMarkingTextLines.configurationTextValues
          : undefined,
        showNameMarkingTextLines: nameMarkingTextLines?.configurationTextValues ? true : false,
      };
    });
  }

  private convertPyOrderLineParameters(
    source: Occ.PyOrderLineParameters
  ): Pick<OrderEntry, 'linePriceIncludingShareOfOrderFees' | 'comparativePrice' | 'cutting' | 'reelCutting' | 'prePrintedLabel'> {
    if (source) {
      const { cutting, reelCutting, prePrintedLabel, linePriceDataIncludingShareOfOrderFees, comparativePriceData, ...target } =
        source;
      return {
        ...target,
        linePriceIncludingShareOfOrderFees: linePriceDataIncludingShareOfOrderFees,
        comparativePrice: this.convertComparativePriceData(comparativePriceData),
        cutting: this.converter.convert(cutting, CUTTING_NORMALIZER),
        reelCutting: this.converter.convert(reelCutting, REEL_CUTTING_NORMALIZER),
        prePrintedLabel: this.converter.convert(prePrintedLabel, PRE_PRINTED_LABEL_NORMALIZER),
      };
    } else {
      return undefined;
    }
  }

  private convertComparativePriceData(source: Occ.OrderEntryComparativePrice): OrderEntryComparativePrice {
    if (source) {
      const { priceData, ...target } = source;
      return {
        ...target,
        price: priceData,
      };
    } else {
      return undefined;
    }
  }

  private convertQuantity(source: Occ.OrderEntry): number {
    return source && source.unitsData && source.unitsData.customerSelectedUnitQty
      ? source.unitsData.customerSelectedUnitQty
      : source.quantity;
  }

  private convertUnitsData(source: Occ.OrderEntry): Unit {
    return source && source.unitsData && source.unitsData.customerSelectedUnit
      ? source.unitsData.customerSelectedUnit
      : source.article?.unit;
  }

  private convertAutoResolveChange(source: Occ.AutoResolveChange): AutoResolveChange {
    return source && { ...source, changeType: source.changeType as AutoResolveChangeType };
  }

  private convertResolveOptions(source: Occ.ResolveOption[]): ResolveOption[] {
    return source
      ? source
          .map((resolveOption) => this.convertResolveOption(resolveOption))
          .filter(({ action }) => Object.values(ResolveOptionActionType).includes(action))
          .filter((resolveOption) => {
            const isAlternativeWithMissingAlternatives =
              resolveOption.action === ResolveOptionActionType.ALTERNATIVE_ARTICLE &&
              resolveOption.alternativeArticles.length === 0;
            return !isAlternativeWithMissingAlternatives;
          })
      : [];
  }

  private convertResolveOption(source: Occ.ResolveOption): ResolveOption {
    const { alternativeArticles, action, ...target } = source;
    return {
      ...target,
      action: (<any>action) as ResolveOptionActionType,
      alternativeArticles: this.converter.convertMany(alternativeArticles, ORDER_ENTRY_NORMALIZER),
    };
  }

  private convertDiscount(pyOrderLineParameters: Occ.PyOrderLineParameters, source: Occ.OrderEntry): boolean {
    return (
      !pyOrderLineParameters?.splitOrderEntry &&
      !source.a4Sample &&
      !source.originalSample &&
      source.cartPrice?.value > source.totalPrice?.value
    );
  }
}
